import type { NotificationData } from "shared/schema";
import { useEffect } from "react";
import { type Item, call, splitEntity } from "shelving";
import { READ_NOTIFICATIONS } from "shared/api";
import { DialogButton, List, PathButton, When } from "shared/react";
import { pluralizeCompactQuantity } from "shared/util/number";
import { ChallengeDialog } from "../challenge/ChallengeDialog.js";
import { EntityName, ProfileName } from "../entity/EntityName.js";
import { callAPI } from "../util/api.js";
import { useItem, useQuery } from "../util/db.js";
import { USER } from "../util/user.js";

export function NotificationList(): React.ReactElement {
	const uid = USER.data.uid;
	const { noticeDate } = useItem("user", uid).data;
	const store = useQuery("notification", { uid, $order: "!date" });
	useEffect(() => {
		for (const { date } of store) {
			if (date > noticeDate) {
				call(callAPI, READ_NOTIFICATIONS, undefined);
				break;
			}
		}
	}, [store]);
	return (
		<List //
			store={store}
			mapper={data => <NotificationItem key={data.id} {...data} />}
		/>
	);
}

function NotificationItem({ action, target, who, count, date }: Item<NotificationData>): React.ReactElement | null {
	const [type, id] = splitEntity(target);
	const uid = USER.data.uid;
	const prefix = (
		<>
			<When target={date} />: {!who ? "Someone" : who === uid ? "You" : <ProfileName uid={who} />}
			{count > 1 ? ` and ${pluralizeCompactQuantity(count - 1, "other", "others")}` : ""}
		</>
	);
	return action === "reply" ? (
		<PathButton path={`/${type}/${id}`}>
			{prefix} replied to {type} <EntityName entity={target} />
		</PathButton>
	) : action === "reaction" ? (
		<PathButton path={`/${type}/${id}`}>
			{prefix} reacted to {type} <EntityName entity={target} />
		</PathButton>
	) : action === "mention" ? (
		<PathButton path={`/${type}/${id}`}>
			{prefix} mentioned you in {type} <EntityName entity={target} />
		</PathButton>
	) : action === "follow" ? (
		<PathButton path="/me/followers">{prefix} followed you</PathButton>
	) : action === "followRequest" ? (
		<PathButton path="/requests">{prefix} wants to follow you</PathButton>
	) : action === "followApprove" ? (
		<PathButton path={`/${type}/${id}`}>{prefix} approved your follow request</PathButton>
	) : action === "groupPending" ? (
		<PathButton path={`/${type}/${id}/members/pending`}>
			{prefix} requested to join <EntityName entity={target} />
		</PathButton>
	) : action === "groupInvite" ? (
		<PathButton path="/invites">
			{prefix} invited you to join <EntityName entity={target} />
		</PathButton>
	) : action === "groupApprove" ? (
		<PathButton path={`/${type}/${id}`}>
			{prefix} approved your request to join <EntityName entity={target} />
		</PathButton>
	) : action === "challengeProgress" ? (
		<DialogButton dialog={<ChallengeDialog id={id} />}>
			{prefix} made progress on <EntityName entity={target} />
		</DialogButton>
	) : action === "challengeComplete" ? (
		<DialogButton dialog={<ChallengeDialog id={id} />}>
			{prefix} completed <EntityName entity={target} />
		</DialogButton>
	) : (
		action // Never happens.
	);
}
